import { useEffect } from 'react';

import { addBusinessDays, addDays, addMonths, eachWeekendOfInterval, getDay, parseISO } from 'date-fns';
import { Field, useField, useForm } from 'react-jeff';
import { useUpgradableBoxes } from './useUpgradableBoxes';
import { isValid } from 'date-fns';
import { JSONConfigService } from '../services/jsonConfig';
import { KonakartService } from '../services/konakart';
import { useUpgradableBoxesStore } from '../store/upgradableBoxesStore';
import { MyAccountSWR } from '../swr/myaccount';
import { useJSONConfig } from './useJSONConfig';
import { validateAlternateName, validateAreaCode, validateDogs, validateInstallationAddress, validateInstallationTime, validateRequiredDate, validateTelephoneNumber, } from './validations';
import { InstallationTime, InstallationTimeCodeKeys, InstallationTimeCodes, NullableAddressLookup, NullableDate, T_Customer, YesNoOptionCheckCodes } from '../types';
import { InitialAccountDetails } from '../store/accountDetailsStore';
import { FaultSwapEvents } from '../segment';
import { getContactName, getServiceAddress } from './customer';
import { ownershipMap } from '../config';

function useUpgradableBoxesForm(
  configService: JSONConfigService,
  kkService: KonakartService,
  myAccountSWR: MyAccountSWR,
  slots: boolean,
  customer?: T_Customer,
  isCorrectAddress?: boolean
) {
  // store
  const { setDetails, details, errorAvailableDates } = useUpgradableBoxesStore();
  // hooks
  const config = useJSONConfig(configService);
  const dayAfterTomorrowDate: Date = addDays(new Date(), 2);
  const maxInstallationDate: Date = addMonths(dayAfterTomorrowDate, 3);
  const primary = customer?.Parties.find(p => p.types['MP:Primary'] !== undefined)?.types?.['MP:Primary'];
  const fields = [];
  const mainContactAvailability = details.mainContactAvailability ? details.mainContactAvailability : YesNoOptionCheckCodes.YES.value;
  const excludeInstallationSundayDates: Date[] = eachWeekendOfInterval({
    start: dayAfterTomorrowDate,
    end: maxInstallationDate,
  }).filter(date => getDay(date) === 0);
  const excludeDates = ((config?.excludeInstallationDates ?? []) as string[])
    .map(str => parseISO(str))
    .concat(excludeInstallationSundayDates);

  const installationDetailsAlternateNameField = useField({
    defaultValue: details.alternateName,
    required: true,
    validations: [validateAlternateName],
  });

  const installationDetailsAreaCodeField = useField({
    defaultValue: details.areaCode,
    required: true,
    validations: [validateAreaCode],
  });

  const installationDetailsPhoneNumberField = useField({
    defaultValue: details.phoneNumber,
    required: true,
    validations: [validateTelephoneNumber],
  });


  const installationDetailsMainContactAvailabilityField = useField({
    defaultValue: mainContactAvailability,
    required: true,
    validations: [],
  });

  const installationDetailsDogsField = useField({
    defaultValue: details.dogs,
    required: true,
    validations: [validateDogs],
  });

  const installationDetailsAlternateAreaCodeField = useField({
    defaultValue: details.alternateAreaCode,
    required: true,
    validations: [validateAreaCode],
  });

  const installationDetailsTechNotesField = useField({
    defaultValue: details.techNotes,
    required: false,
    validations: [],
  });

  const installationDetailsAlternatePhoneNumberField = useField({
    defaultValue: details.alternatePhoneNumber,
    required: true,
    validations: [validateTelephoneNumber],
  });

  const installationDetailsInstallationDateField = useField<NullableDate>({
    // The value field in the React Native date-picker cannot be null, so adding a default value here.
    defaultValue: null,
    required: true,
    // React Native date picker can also not exclude dates. This is checked here.
    validations: [validateRequiredDate('Installation Date')],
  });

  const installationDetailsInstallationTimeField = useField<InstallationTime>({
    defaultValue: InstallationTimeCodeKeys.AM,
    required: true,
    validations: [validateInstallationTime],
  });

  const installationDetailsInstallationAddressField = useField<NullableAddressLookup>({
    defaultValue: details.serviceAddress,
    required: true,
    validations: [validateInstallationAddress],
  })

  if (slots === true) {
    fields.push(
      installationDetailsInstallationDateField,
      installationDetailsInstallationTimeField,
      installationDetailsDogsField,
    )
  }
  if (isCorrectAddress === false) {
    fields.push(installationDetailsInstallationAddressField);
  }
  customer && fields.push(
    installationDetailsAreaCodeField,
    installationDetailsPhoneNumberField,
  );

  details.mainContactAvailability === YesNoOptionCheckCodes.NO.value && fields.push(
    installationDetailsAlternateNameField,
    installationDetailsAlternatePhoneNumberField,
    installationDetailsAlternateAreaCodeField
  )
  // Form - validation component
  const form = useForm({
    fields,
    onSubmit: () => { }, // nothing to submit
  });

  /**
   * On load set the installation time and date based on store value
   */
  useEffect(() => {
    if (isValid(details.installationDate)) {
      installationDetailsInstallationDateField.setValue(details.installationDate);
    }
    installationDetailsInstallationTimeField.setValue(details.installationTime);
  }, []);

  // prefill fields if avialable
  useEffect(() => {
    if (!customer) return;
    installationDetailsAreaCodeField.setValue((details.areaCode && details.mainContactAvailability === YesNoOptionCheckCodes.YES.value) ? details.areaCode : primary?.contactDetails?.[0].areaCode ?? '');
    installationDetailsPhoneNumberField.setValue((details.phoneNumber && details.mainContactAvailability === YesNoOptionCheckCodes.YES.value) ? details.phoneNumber : primary?.contactDetails?.[0].number ?? '')
  }, [customer, installationDetailsMainContactAvailabilityField.value])

  /**
   * Handles the upgrade box store to manage the date / time information stored
   */
  useEffect(() => {
    setDetails({
      isValid: form.valid,
      mainContactAvailability: installationDetailsMainContactAvailabilityField.value,
      dogs: installationDetailsDogsField.value,
      alternateName: installationDetailsAlternateNameField.value,
      alternateAreaCode: installationDetailsAlternateAreaCodeField.value,
      alternatePhoneNumber: installationDetailsAlternatePhoneNumberField.value,
      techNotes: installationDetailsTechNotesField.value,
      areaCode: installationDetailsAreaCodeField.value,
      phoneNumber: installationDetailsPhoneNumberField.value,
      serviceAddress: installationDetailsInstallationAddressField.value,
      installationDate:
        installationDetailsInstallationDateField.value == null
          ? addDays(new Date(), 2)
          : installationDetailsInstallationDateField.value,
      installationTime: installationDetailsInstallationTimeField.value,
      boxInstallRequired: true,
      slots: slots,
      currentAddress: customer ? getServiceAddress(customer) : '',
      fullName: customer ? getContactName(customer) : '',
      ownership: customer ? ownershipMap[customer.type.toLowerCase()] || "" : "",
    });
  }, [
    form.valid,
    installationDetailsInstallationDateField.value,
    installationDetailsInstallationTimeField.value,
    installationDetailsAlternateNameField.value,
    installationDetailsAlternateAreaCodeField.value,
    installationDetailsAlternatePhoneNumberField.value,
    installationDetailsMainContactAvailabilityField.value,
    installationDetailsDogsField.value,
    installationDetailsTechNotesField.value,
    installationDetailsInstallationAddressField.value,
    installationDetailsAreaCodeField.value,
    installationDetailsPhoneNumberField.value,
    setDetails,
    errorAvailableDates,
    slots,
    customer
  ]);

  return {
    dayAfterTomorrowDate,
    maxInstallationDate,
    form,
    fields,
    installationDetailsInstallationDateField,
    installationDetailsInstallationTimeField,
    installationDetailsAlternateNameField,
    installationDetailsAlternateAreaCodeField,
    installationDetailsAlternatePhoneNumberField,
    installationDetailsMainContactAvailabilityField,
    installationDetailsTechNotesField,
    installationDetailsDogsField,
    installationDetailsInstallationAddressField,
    installationDetailsAreaCodeField,
    installationDetailsPhoneNumberField
  };
}

export { useUpgradableBoxesForm };
